import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { RouteComponentProps } from "react-router";
import { generatePath, withRouter } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import TargetZoneStore from "stores/TargetZoneStore";
import LayoutContainer from "components/Layout/Container";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { SNACKBAR_TYPES } from "components/Snackbar";
import ROUTES from "navigation/routes";
import { TableSortType } from "stores/templates/TableStore";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  CardWrapper,
  OverflowLoader,
  ScrollLoader,
} from "./styles";

import TargetZoneCard from "./TargetZoneCard";
import { constants } from "./constants";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class TargetZone extends React.Component<Props> {
  @computed get items() {
    return TargetZoneStore.items;
  }

  @computed get isListEmpty() {
    return this.items.length === 0;
  }

  componentDidMount() {
    TargetZoneStore.fetch();
  }

  componentWillUnmount() {
    TargetZoneStore.resetPage();
  }

  fetchMoreItems = async () => {
    const { setMessage } = this.props;

    try {
      await TargetZoneStore.fetchMore();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    TargetZoneStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && TargetZoneStore.fetch();
  };

  onSearchClear = () => {
    TargetZoneStore.clearSearch();
    TargetZoneStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    TargetZoneStore.applyOrder(value);
    TargetZoneStore.resetPage();
    TargetZoneStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      TargetZoneStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    TargetZoneStore.resetPage();
    TargetZoneStore.fetch();
  };

  onFilterClear = () => {
    TargetZoneStore.clearFilters();
    TargetZoneStore.fetch();
  };

  onAddItemClick = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_TARGET_ZONES_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (item: any) => () => {
    const path = generatePath(ROUTES.CONTENT_TARGET_ZONES_DETAILS, {
      id: item.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (item: any) => () => {
    const path = generatePath(ROUTES.CONTENT_TARGET_ZONES_DETAILS, {
      id: item.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onPublish = (item: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = item;

    try {
      await TargetZoneStore.changeStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <LayoutContainer header={<Header controls={<PublishButton />} />}>
        <Page
          title={constants.targetZone.title}
          controls={
            <>
              <PageControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={this.onAddItemClick}
                >
                  {constants.targetZone.addButtonTitle}
                </Button>
              </PageControl>
            </>
          }
          isLoading={TargetZoneStore.isTargetZoneLoading}
        >
          <SearchContainer>
            <Search
              initValue={TargetZoneStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={TargetZoneStore.orderContext.appliedOrderInfo}
                  onApply={this.onSortApply}
                />
              </SearchControlWrapper>
              <SearchControlWrapper>
                <FilteringControl
                  initValue={TargetZoneStore.filters}
                  onApply={this.onFilterApply}
                  onClear={this.onFilterClear}
                  statusFiltering
                />
              </SearchControlWrapper>
            </SearchControls>
          </SearchContainer>

          {(TargetZoneStore.isTargetZoneLoading ||
            TargetZoneStore.isTargetZonePublishing) && <OverflowLoader />}
          {!this.isListEmpty ? (
            <InfiniteScroll
              dataLength={this.items.length}
              hasMore={TargetZoneStore.hasMoreInfo}
              next={this.fetchMoreItems}
              height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
              loader={<ScrollLoader />}
            >
              {this.items.map(item => (
                <CardWrapper key={item.id}>
                  <TargetZoneCard
                    title={item.title}
                    image={item.image}
                    publishStatus={item.publishStatus}
                    onDetailsClick={this.onDetailsClick(item)}
                    onEdit={this.onEdit(item)}
                    onPublish={this.onPublish(item)}
                  />
                </CardWrapper>
              ))}
            </InfiniteScroll>
          ) : (
            <Placeholder
              height={280}
              width={280}
              text={
                TargetZoneStore.search
                  ? constants.targetZone.noRecordsMatch
                  : constants.targetZone.noRecords
              }
            />
          )}
        </Page>
      </LayoutContainer>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(TargetZone)));
